"use client";
import eventBus from "@/lib/_event-bus/event-bus";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { getPageViewInfo } from "@/lib/auth/auth-utils";
import { ClientSessionData, convertToClientSession } from "@/lib/auth/session";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useContext, useEffect, useRef } from "react";
import { useDataLayerContext } from "./DataLayerContextProvider";
import { DataLayerEvents } from "./DataLayerContextProvider";

interface Props {
	pageType: PageType;
	userId?: number | undefined;
	companyName?: string | undefined;
	currencyCode?: string | null;
	accountNumber?: string | null;
}

type PageType =
	| "page"
	| "product"
	| "cart"
	| "checkout"
	| "company"
	| "accounts"
	| "informational"
	| "regional"
	| "blogs"
	| "news"
	| "404";

export default function PageViewEvent({ pageType }: Props) {
	// useSearchParams must be rendered under a Suspense boundary for
	// statically generated pages. Wrapping here keeps that requirement
	// self-contained instead of needing <Suspense> at every usage site.
	return (
		<Suspense fallback={null}>
			<PageViewEventInner pageType={pageType} />
		</Suspense>
	);
}

function PageViewEventInner({ pageType }: Props) {
	const { push } = useDataLayerContext();
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);
	const session = useSessionContext();
	const userId = session.session?.user.bcCustomerId;

	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();

	const lastRoute = useRef<string>("");

	useEffect(() => {
		const route = `${pathname}${search ? `?${search}` : ""}`;

		if (lastRoute.current === route) {
			return;
		}

		const fetchDataAndSendEvent = async () => {
			try {
				const res = await getPageViewInfo(); // Adjust API endpoint
				const { companyName, accountNumber } = res;

				const eventPayload: Record<string, any> = {
					event: DataLayerEvents.PAGE_VIEW,
					userId: userId !== 0 ? userId : undefined,
					companyName,
					pageType,
					currencyCode: currencyFormattingRules?.currency ?? undefined,
					accountNumber,
				};

				push(eventPayload);
			} catch (error) {
				console.error("Error fetching company info:", error);
			}
		};

		const prevPath = lastRoute.current.split("?")[1];

		// only do this if the path has changed
		if (pathname !== prevPath) {
			fetchDataAndSendEvent();
		}

		// fire the event into the event bus
		eventBus.dispatch("pageView", {
			path: route,
			pageType,
			session: convertToClientSession(session.session),
		});

		lastRoute.current = route;
	}, [userId, pathname, search]); // Runs when userId, path, or search params change

	return null;
}
